/**
 * @file DatabaseMappingModal.js
 * WHAT: Reusable create/edit modal for an app_database_mapping row — Basic Information,
 *       Deployment Configuration, read-only auto-discovered Topology, MSSQL Configuration
 *       (instance + target databases), and Notes.
 * WHY:  Both the Database Mappings screen (AppDatabaseMapping.js) and the DB Compliance
 *       Matrix author mappings with the identical field set / validation / deployment-type
 *       options / MSSQL instance picker. Centralizing the modal here keeps a single source
 *       of truth so the two surfaces cannot drift.
 *
 * CONTRACT: this is a CONTROLLED, PRESENTATIONAL component. It does NOT persist anything.
 *   - The parent owns the form state (`formData`) and a setter (`onFormChange(field, value)`).
 *   - On Save it calls `onSave()`; the PARENT decides which endpoint to hit
 *     (app-database-mappings/create|update vs db-compliance-matrix/<tech>/mappings/...).
 *   - It owns ONLY its local, presentational MSSQL discovery state (the discovered-instance
 *     datalist + the target-database checkbox list), fetched from the same endpoints the
 *     old inline modal used. When the instance/include-system changes it refetches and
 *     clears the target list — same behavior as before.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

// Deployment type configurations per database type (shared with AppDatabaseMapping.js).
export const DEPLOYMENT_TYPES = {
  Oracle: [
    { value: 'STANDALONE', label: 'Standalone' },
    { value: 'RAC', label: 'Oracle RAC' },
    { value: 'STANDBY', label: 'Standby/Data Guard' }
  ],
  MSSQL: [
    { value: 'STANDALONE', label: 'Standalone' },
    { value: 'ALWAYS_ON', label: 'Always On AG' },
    { value: 'FAILOVER_CLUSTER', label: 'Failover Cluster' },
    { value: 'AZURE_SQL', label: 'Azure SQL Database' }
  ],
  MySQL: [
    { value: 'STANDALONE', label: 'Standalone' },
    { value: 'REPLICATION', label: 'Replication (Master-Replica)' }
  ],
  PostgreSQL: [
    { value: 'STANDALONE', label: 'Standalone' },
    { value: 'REPLICATION', label: 'Streaming Replication' }
  ],
  Cassandra: [
    { value: 'CLUSTER', label: 'Cluster' }
  ]
};

// Normalize a database type string to a DEPLOYMENT_TYPES key.
export const normalizeDbType = (dbType) => {
  if (!dbType) return 'Oracle';
  const upper = dbType.toUpperCase();
  if (upper === 'ORACLE') return 'Oracle';
  if (upper === 'MSSQL' || upper === 'SQLSERVER') return 'MSSQL';
  if (upper === 'MYSQL') return 'MySQL';
  if (upper === 'POSTGRESQL' || upper === 'POSTGRES') return 'PostgreSQL';
  if (upper === 'CASSANDRA') return 'Cassandra';
  return 'Oracle';
};

// Coerce a JSONB array field (may arrive as a JSON string) to a real JS array.
export const asArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Props:
 *   show           : boolean — render the modal
 *   onClose        : () => void
 *   mode           : 'create' | 'edit'  (controls header + whether the app_id picker shows)
 *   formData       : the controlled form object (see editFormData shape in the parent)
 *   onFormChange   : (field, value) => void — parent updates its form state
 *   applications   : [{app_id, app_name}] for the create-mode owner dropdown
 *   databaseType   : the tech for this mapping (drives deployment-type options + MSSQL section)
 *   saving         : boolean — disables Save while the parent persists
 *   onSave         : () => void — parent persists (decides the endpoint)
 *   error          : optional string shown in an alert inside the modal body
 *   appLocked      : boolean — when true the app_id is fixed (edit mode); when false show picker
 */
const DatabaseMappingModal = ({
  show,
  onClose,
  mode = 'edit',
  formData,
  onFormChange,
  applications = [],
  databaseType,
  saving = false,
  onSave,
  error = null,
  appLocked = false,
}) => {
  // Local, presentational MSSQL discovery state.
  const [mssqlInstanceDatabases, setMssqlInstanceDatabases] = useState([]);
  const [loadingMssqlDatabases, setLoadingMssqlDatabases] = useState(false);
  const [mssqlDiscoveredInstances, setMssqlDiscoveredInstances] = useState([]);
  const [loadingMssqlInstances, setLoadingMssqlInstances] = useState(false);

  const dbType = formData?.database_type || databaseType || 'Oracle';
  const isMssql = (dbType || '').toUpperCase() === 'MSSQL';

  const fetchMssqlDiscoveredInstances = useCallback(async () => {
    setLoadingMssqlInstances(true);
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('mssql/instances/'));
      if (response.data.success) {
        setMssqlDiscoveredInstances(response.data.instances || []);
      }
    } catch (err) {
      console.error('Failed to fetch MSSQL discovered instances:', err);
      setMssqlDiscoveredInstances([]);
    } finally {
      setLoadingMssqlInstances(false);
    }
  }, []);

  const fetchMssqlInstanceDatabases = useCallback(async (instanceIdentifier, includeSystem = false) => {
    if (!instanceIdentifier) {
      setMssqlInstanceDatabases([]);
      return;
    }
    setLoadingMssqlDatabases(true);
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('mssql/instance-databases/'),
        { params: { instance_identifier: instanceIdentifier, include_system: includeSystem } }
      );
      if (response.data.success) {
        setMssqlInstanceDatabases(response.data.databases || []);
      }
    } catch (err) {
      console.error('Failed to fetch MSSQL instance databases:', err);
      setMssqlInstanceDatabases([]);
    } finally {
      setLoadingMssqlDatabases(false);
    }
  }, []);

  // On open (or when the modal target changes), load MSSQL discovery for MSSQL mappings —
  // mirroring the old handleEditMapping/handleAddNew behavior. Keyed by the row's identity
  // so reopening for a different mapping refetches; we also seed the target-DB list for an
  // edit so the existing selection's checkboxes render.
  useEffect(() => {
    if (!show) {
      setMssqlInstanceDatabases([]);
      return;
    }
    if (isMssql) {
      fetchMssqlDiscoveredInstances();
      if (formData?.mssql_instance_name) {
        fetchMssqlInstanceDatabases(formData.mssql_instance_name, formData.mssql_include_system_dbs || false);
      } else {
        setMssqlInstanceDatabases([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isMssql, formData?.database_name, formData?.database_identifier]);

  if (!show || !formData) return null;

  // Field change helper mirroring the parent's handleInputChange behavior, including the
  // auto-generation of database_identifier from display_name in create/un-set-identifier mode.
  const handleField = (name, value) => {
    onFormChange(name, value);
    if (name === 'display_name' && (mode === 'create' || !appLocked) && !formData.database_identifier) {
      onFormChange('database_identifier', value.replace(/[^A-Za-z0-9_]/g, '_').toUpperCase());
    }
  };

  const topo = formData.node_topology;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
      <div style={{ padding: '20px' }}>
        <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className={`fas ${mode === 'edit' ? 'fa-edit' : 'fa-plus'} me-2`}></i>
              {mode === 'edit'
                ? `Edit ${dbType} Database - ${formData.database_identifier}`
                : `Add New ${dbType} Database`}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {error && (
              /* display:block shields against the global .alert{display:flex}
                 bleed from DatabaseRefresh.css/AzureADGroups.css */
              <div className="alert alert-danger" style={{ display: 'block' }}>
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            <form>
              {/* Basic Information */}
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  Basic Information
                </h6>
                <div className="row">
                  {!appLocked && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Primary Application <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        name="app_id"
                        value={formData.app_id || ''}
                        onChange={(e) => onFormChange('app_id', e.target.value)}
                        required
                      >
                        <option value="">Select Application</option>
                        {applications.map(app => (
                          <option key={app.app_id} value={app.app_id}>
                            {app.app_id} - {app.app_name}
                          </option>
                        ))}
                      </select>
                      {/* M:N: creation registers the database under its PRIMARY app;
                          additional applications are linked from the matrix row. */}
                      <div className="form-text">
                        The primary application's owner attests this database. You can link
                        more applications from the database's row afterward.
                      </div>
                    </div>
                  )}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Database Identifier <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="database_identifier"
                      value={formData.database_identifier}
                      onChange={(e) => handleField('database_identifier', e.target.value)}
                      placeholder="e.g., HRPROD, FIN_CLUSTER"
                    />
                    <small className="text-muted">Unique identifier (uppercase, no spaces)</small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Display Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="display_name"
                      value={formData.display_name}
                      onChange={(e) => handleField('display_name', e.target.value)}
                      placeholder="e.g., HR Production Database"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Database Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="database_name"
                      value={formData.database_name}
                      onChange={(e) => handleField('database_name', e.target.value)}
                      placeholder="Technical database name"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Environment</label>
                    <select
                      className="form-select"
                      name="database_environment"
                      value={formData.database_environment}
                      onChange={(e) => handleField('database_environment', e.target.value)}
                    >
                      <option value="">Select Environment</option>
                      <option value="PRODUCTION">PRODUCTION</option>
                      <option value="STAGING">STAGING</option>
                      <option value="TEST">TEST</option>
                      <option value="DEVELOPMENT">DEVELOPMENT</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Deployment Configuration */}
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-server me-2"></i>
                  Deployment Configuration
                </h6>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Deployment Type</label>
                    <select
                      className="form-select"
                      name="deployment_type"
                      value={formData.deployment_type}
                      onChange={(e) => handleField('deployment_type', e.target.value)}
                    >
                      {DEPLOYMENT_TYPES[normalizeDbType(dbType)].map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Database Version</label>
                    <input
                      type="text"
                      className="form-control"
                      name="database_version"
                      value={formData.database_version}
                      onChange={(e) => handleField('database_version', e.target.value)}
                      placeholder="e.g., 19c, 2019, 8.0"
                    />
                  </div>
                </div>
              </div>

              {/* Topology Info (read-only, auto-discovered) */}
              {topo && (
                <div className="mb-4">
                  <h6 className="border-bottom pb-2 mb-3">
                    <i className="fas fa-network-wired me-2"></i>
                    Topology Info
                    <small className="text-muted ms-2">(Auto-discovered)</small>
                  </h6>
                  <div className="bg-light p-3 rounded">
                    {/* Oracle RAC / Cluster Nodes */}
                    {topo.nodes && topo.nodes.length > 0 && (
                      <div className="mb-3">
                        <strong className="d-block mb-2">
                          <i className="fas fa-server me-1"></i>
                          {formData.deployment_type === 'RAC' ? 'RAC Nodes' : 'Cluster Nodes'}
                          <span className="badge bg-info ms-2">{topo.nodes.length}</span>
                        </strong>
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered mb-0">
                            <thead>
                              <tr className="table-secondary">
                                <th className="text-muted">Instance</th>
                                <th className="text-muted">Host</th>
                                <th className="text-muted">Port</th>
                                <th className="text-muted">Role</th>
                                <th className="text-muted">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {topo.nodes.map((node, idx) => (
                                <tr key={node.node_id || idx}>
                                  <td><code>{node.instance_name || node.node_id}</code></td>
                                  <td>{node.host}</td>
                                  <td>{node.port}</td>
                                  <td>
                                    <span className={node.role === 'PRIMARY' ? 'text-success' : 'text-secondary'}>
                                      {node.role}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={node.status === 'ACTIVE' ? 'text-success' : 'text-warning'}>
                                      {node.status || 'Unknown'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Oracle Standby Database */}
                    {topo.standby && (
                      <div className="mb-3">
                        <strong className="d-block mb-2">
                          <i className="fas fa-clone me-1"></i>
                          Standby Database
                        </strong>
                        <div className="row">
                          <div className="col-md-6">
                            <small className="text-muted">DB Name:</small>
                            <div><code>{topo.standby.db_unique_name}</code></div>
                          </div>
                          <div className="col-md-6">
                            <small className="text-muted">Host:</small>
                            <div>{topo.standby.host}</div>
                          </div>
                          <div className="col-md-6 mt-2">
                            <small className="text-muted">Role:</small>
                            <div><span className="text-warning">{topo.standby.role}</span></div>
                          </div>
                          <div className="col-md-6 mt-2">
                            <small className="text-muted">Protection Mode:</small>
                            <div>{topo.standby.protection_mode}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SCAN/Listener Info */}
                    {(topo.scan_name || topo.listener_name) && (
                      <div className="mb-3">
                        <strong className="d-block mb-2">
                          <i className="fas fa-broadcast-tower me-1"></i>
                          {topo.scan_name ? 'SCAN Configuration' : 'Listener Configuration'}
                        </strong>
                        <div className="row">
                          <div className="col-md-6">
                            <small className="text-muted">{topo.scan_name ? 'SCAN Name' : 'Listener'}:</small>
                            <div><code>{topo.scan_name || topo.listener_name}</code></div>
                          </div>
                          <div className="col-md-6">
                            <small className="text-muted">Port:</small>
                            <div>{topo.scan_port || topo.listener_port || 1521}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Service Name */}
                    {topo.service_name && (
                      <div>
                        <small className="text-muted">Service Name:</small>
                        <div><code>{topo.service_name}</code></div>
                      </div>
                    )}

                    {/* Cassandra Cluster Info */}
                    {topo.cluster_name && (
                      <div className="row">
                        <div className="col-md-4">
                          <small className="text-muted">Cluster Name:</small>
                          <div><code>{topo.cluster_name}</code></div>
                        </div>
                        <div className="col-md-4">
                          <small className="text-muted">Keyspace:</small>
                          <div><code>{topo.keyspace}</code></div>
                        </div>
                        <div className="col-md-4">
                          <small className="text-muted">Local DC:</small>
                          <div>{topo.local_datacenter}</div>
                        </div>
                      </div>
                    )}

                    {/* Cassandra Contact Points */}
                    {topo.contact_points && topo.contact_points.length > 0 && (
                      <div className="mt-3">
                        <strong className="d-block mb-2">
                          <i className="fas fa-plug me-1"></i>
                          Contact Points
                          <span className="badge bg-info ms-2">{topo.contact_points.length}</span>
                        </strong>
                        <div className="d-flex flex-wrap gap-2">
                          {topo.contact_points.map((cp, idx) => (
                            <code key={idx} className="text-muted">{cp.host}:{cp.port}</code>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <small className="text-muted mt-2 d-block">
                    <i className="fas fa-info-circle me-1"></i>
                    Topology is auto-discovered when connecting to the database during refresh jobs.
                  </small>
                </div>
              )}

              {/* MSSQL-specific fields */}
              {isMssql && (
                <div className="mb-4">
                  <h6 className="border-bottom pb-2 mb-3">
                    <i className="fas fa-cog me-2"></i>
                    MSSQL Configuration
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">SQL Server Instance</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          name="mssql_instance_name"
                          value={formData.mssql_instance_name}
                          onChange={(e) => {
                            onFormChange('mssql_instance_name', e.target.value);
                            onFormChange('mssql_target_databases', []);
                            setMssqlInstanceDatabases([]);
                          }}
                          placeholder="Instance name"
                          list="mssql-instances-list"
                        />
                        <datalist id="mssql-instances-list">
                          {mssqlDiscoveredInstances.map(inst => (
                            <option key={inst.instance_identifier} value={inst.instance_identifier}>
                              {inst.display_name}
                            </option>
                          ))}
                        </datalist>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            if (formData.mssql_instance_name) {
                              fetchMssqlInstanceDatabases(formData.mssql_instance_name, formData.mssql_include_system_dbs);
                            }
                          }}
                          disabled={!formData.mssql_instance_name || loadingMssqlDatabases}
                        >
                          {loadingMssqlDatabases ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="fas fa-sync-alt"></i>
                          )}
                        </button>
                      </div>
                      {loadingMssqlInstances && (
                        <small className="text-info">Loading instances...</small>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Include System Databases</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.mssql_include_system_dbs}
                          onChange={(e) => {
                            onFormChange('mssql_include_system_dbs', e.target.checked);
                            onFormChange('mssql_target_databases', []);
                            if (formData.mssql_instance_name) {
                              fetchMssqlInstanceDatabases(formData.mssql_instance_name, e.target.checked);
                            }
                          }}
                        />
                        <label className="form-check-label">Show system databases</label>
                      </div>
                    </div>
                    {mssqlInstanceDatabases.length > 0 && (
                      <div className="col-12">
                        <label className="form-label">Target Databases</label>
                        <div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {mssqlInstanceDatabases.map(db => (
                            <div key={db.database_name} className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={asArray(formData.mssql_target_databases).includes(db.database_name)}
                                onChange={(e) => {
                                  const current = asArray(formData.mssql_target_databases);
                                  if (e.target.checked) {
                                    onFormChange('mssql_target_databases', [...current, db.database_name]);
                                  } else {
                                    onFormChange('mssql_target_databases', current.filter(d => d !== db.database_name));
                                  }
                                }}
                              />
                              <label className="form-check-label">{db.database_name}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => handleField('notes', e.target.value)}
                  placeholder="Additional notes..."
                />
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              <i className="fas fa-times me-2"></i>Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
              ) : (
                <><i className="fas fa-save me-2"></i>Save</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseMappingModal;
